import React, { useEffect, useRef, useState } from "react";
import 'react-modern-drawer/dist/index.css';
import Chart from "chart.js/auto";
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { fmesh } from '../../../core/globalVariables.js';

const years = [
    "2024-01-01T00:00:00+00:00",
    "2027-01-01T00:00:00+00:00",
    "2029-01-01T00:00:00+00:00",
    "2031-01-01T00:00:00+00:00"
];

const ComparePanel = ({
    longstandingGeoJSONRef,
    selectedYearEditing,
    universalParcelDifferentiator,
    compareSelectedKey,
    comparingLandtypeSelectedOption,
    originalGeoJSONforComparingRef,
    isVisible,
    globalRefs,
    globalState
}) => {
    const currentChartRef = useRef(null);
    const yearlyChartRef = useRef(null);
    const [logScale, setLogScale] = useState(false);

    const CompareRef = globalRefs.CompareRef;
    const selectedKey = globalState.selectedKey;
    const selectedMetric = globalState.selectedMetric;

    const initChart = (chartRef, chartId, datasets) => {
        if (chartRef.current) {
            chartRef.current.destroy();
        }
        const ctx = document.getElementById(chartId);
        if (ctx) {
            chartRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: datasets.labels,
                    datasets: datasets.datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: { display: false },
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            type: logScale ? 'logarithmic' : 'linear', // Add logarithmic scaling
                            ticks: {
                                callback: function (value) {
                                    if (logScale) {
                                        return Number(value.toString());
                                    }
                                    return value;
                                }
                            }
                        },
                        x: {
                            display: true,
                            stacked: false,
                            grid: { display: false },
                            ticks: { autoSkip: false, minRotation: 90, maxRotation: 90 },
                            barPercentage: 0.4,
                            categoryPercentage: 0.5
                        }
                    }
                }
            });
        }
    };

    const initChartLine = (lineChartRef, chartId, datasets) => {
        if (lineChartRef.current) {
            lineChartRef.current.destroy();
        }
        const ctx = document.getElementById(chartId);
        if (ctx) {
            lineChartRef.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: datasets.labels,
                    datasets: datasets.datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: { display: false },
                        legend: { display: true }
                    },
                    scales: {
                        y: { beginAtZero: true },
                        x: {
                            display: true,
                            stacked: false,
                            grid: { display: false },
                            ticks: { autoSkip: false, minRotation: 90, maxRotation: 90 }
                        }
                    }
                }
            });
        }
    };

    const parseCumulativeDataByKey = (geojson, selectedYearEditing, compareKey) => {
        const cumulativeDataMap = new Map();

        geojson.features.forEach(feature => {
            const keyValue = feature.feature_data_simple_timeseries[compareKey]?.[selectedYearEditing];
            const metricValue = feature.feature_data_simple_timeseries[selectedMetric]?.[selectedYearEditing] || 0;

            if (!keyValue) return;

            cumulativeDataMap.set(keyValue, (cumulativeDataMap.get(keyValue) || 0) + metricValue);
        });

        const sortedData = Array.from(cumulativeDataMap.entries()).sort((a, b) => b[1] - a[1]);
        return {
            labels: sortedData.map(([key]) => key),
            data: sortedData.map(([, value]) => value)
        };
    };

    const parseYearlyAccumulatedData = (geojson, compareKey) => {
        const yearlyAccumilativeDefiner = comparingLandtypeSelectedOption;
        const accumulatedData = new Map();

        // Determine available years dynamically from the first matching feature
        const firstFeatureWithTimeseries = geojson.features.find(feature =>
            feature.feature_data_simple_timeseries &&
            (feature.feature_data_simple_timeseries[compareKey] || feature.feature_data_simple_timeseries[selectedMetric])
        );

        const dynamicYears = firstFeatureWithTimeseries
            ? Object.keys(firstFeatureWithTimeseries.feature_data_simple_timeseries[compareKey] || firstFeatureWithTimeseries.feature_data_simple_timeseries[selectedMetric])
            : [];

        dynamicYears.forEach(year => {
            geojson.features.forEach(feature => {
                const keyValue = feature.feature_data_simple_timeseries[compareKey]?.[year];
                const metricValue = feature.feature_data_simple_timeseries[selectedMetric]?.[year] || 0;

                if (
                    yearlyAccumilativeDefiner === "" ||
                    keyValue === yearlyAccumilativeDefiner ||
                    yearlyAccumilativeDefiner === "none"
                ) {
                    if (!accumulatedData.has(year)) {
                        accumulatedData.set(year, 0);
                    }
                    accumulatedData.set(year, accumulatedData.get(year) + metricValue);
                }
            });
        });

        const labels = dynamicYears;
        const datasets = [{
            label: `Accumulated Data by ${compareKey} per Year`,
            data: labels.map(year => accumulatedData.get(year) || 0),
        }];

        return { labels, datasets };
    };


    useEffect(() => {
        if (longstandingGeoJSONRef.current && compareSelectedKey) {
            const yearlyAccumulatedData = parseYearlyAccumulatedData(longstandingGeoJSONRef.current, compareSelectedKey);
            const originalYearlyAccumulatedData = parseYearlyAccumulatedData(originalGeoJSONforComparingRef.current, compareSelectedKey);
            initChartLine(yearlyChartRef, 'yearlyChart', {
                labels: yearlyAccumulatedData.labels,
                datasets: [
                    {
                        label: `Longstanding Data by ${compareSelectedKey}`,
                        data: yearlyAccumulatedData.datasets[0].data,
                        borderColor: '#36A2EB',
                        backgroundColor: 'rgba(54, 162, 235, 0.2)',
                        fill: true
                    },
                    {
                        label: `Original Data by ${compareSelectedKey}`,
                        data: originalYearlyAccumulatedData.datasets[0].data,
                        borderColor: '#FF6384',
                        backgroundColor: 'rgba(255, 99, 132, 0.2)',
                        fill: true
                    }
                ]
            });
        }
    }, [comparingLandtypeSelectedOption, selectedKey, compareSelectedKey, selectedMetric]);

    useEffect(() => {
        const geojson = longstandingGeoJSONRef.current;
        const originalGeojson = originalGeoJSONforComparingRef.current;
        if (geojson && originalGeojson && compareSelectedKey) {
            const timeSeriesArray = [];

            fmesh.traverse((child) => {
                if (child.isMesh) {
                    for (let i = 0; i < geojson.features.length; i++) {
                        if (child.name === geojson.features[i].skylark_id) {
                            const timeSeriesData = geojson.features[i].feature_data_simple_timeseries[compareSelectedKey]?.[selectedYearEditing];
                            if (timeSeriesData) {
                                const r = Math.round(child.material.color.r * 255);
                                const g = Math.round(child.material.color.g * 255);
                                const b = Math.round(child.material.color.b * 255);
                                const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
                                const uniqueEntry = { timeSeriesData, color: hexColor };

                                if (!timeSeriesArray.some(entry => entry.timeSeriesData === uniqueEntry.timeSeriesData && entry.color === uniqueEntry.color)) {
                                    timeSeriesArray.push(uniqueEntry);
                                }
                            }
                        }
                    }
                }
            });

            const cumulativeDataLongstanding = parseCumulativeDataByKey(geojson, selectedYearEditing, compareSelectedKey);
            const cumulativeDataOriginal = parseCumulativeDataByKey(originalGeojson, selectedYearEditing, compareSelectedKey);
            const barColorsLongstanding = cumulativeDataLongstanding.labels.map(label => {
                const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                return matchingEntry ? matchingEntry.color : '#36A2EB';
            });
            const barColorsOriginal = cumulativeDataOriginal.labels.map(label => {
                const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                return matchingEntry ? matchingEntry.color : '#FF6384';
            });

            initChart(currentChartRef, 'currentChart', {
                labels: cumulativeDataLongstanding.labels,
                datasets: [
                    {
                        label: `Longstanding Data by ${compareSelectedKey}`,
                        data: cumulativeDataLongstanding.data,
                        backgroundColor: cumulativeDataLongstanding.labels.map(label => {
                            const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                            const color = matchingEntry ? matchingEntry.color : '#36A2EB';
                            return comparingLandtypeSelectedOption === "none" ? color : (comparingLandtypeSelectedOption === label ? color : `${color}80`); // 80 is the hex for 50% opacity
                        })
                    },
                    {
                        label: `Original Data by ${compareSelectedKey}`,
                        data: cumulativeDataOriginal.data,
                        backgroundColor: cumulativeDataLongstanding.labels.map(label => {
                            const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                            const color = matchingEntry ? matchingEntry.color : '#36A2EB';
                            return comparingLandtypeSelectedOption === "none" ? color : (comparingLandtypeSelectedOption === label ? color : `${color}80`); // 80 is the hex for 50% opacity
                        })
                    }
                ]
            });

            let comparingLandtypeSelectedOptionColor;

            for (let i = 0; i < timeSeriesArray.length; i++) {
                if (timeSeriesArray[i].timeSeriesData === comparingLandtypeSelectedOption) {
                    comparingLandtypeSelectedOptionColor = timeSeriesArray[i].color;
                }
            }

            const yearlyAccumulatedData = parseYearlyAccumulatedData(geojson, compareSelectedKey);
            const originalYearlyAccumulatedData = parseYearlyAccumulatedData(originalGeojson, compareSelectedKey);
            initChartLine(yearlyChartRef, 'yearlyChart', {
                labels: yearlyAccumulatedData.labels,
                datasets: [
                    {
                        label: `Updated Data by ${compareSelectedKey}`,
                        data: yearlyAccumulatedData.datasets[0].data,
                        borderColor: '#006400', // Dark Green
                        backgroundColor: 'rgba(0, 100, 0, 0.2)', // Dark Green with 20% opacity
                        fill: true
                    },
                    {
                        label: `Original Data by ${compareSelectedKey}`,
                        data: originalYearlyAccumulatedData.datasets[0].data,
                        borderColor: '#800080', // Purple
                        backgroundColor: 'rgba(128, 0, 128, 0.2)', // Purple with 20% opacity
                        fill: true
                    }
                ]
            }, compareSelectedKey, comparingLandtypeSelectedOptionColor);
        }
    }, [longstandingGeoJSONRef.current, originalGeoJSONforComparingRef.current, compareSelectedKey, selectedYearEditing, selectedMetric, comparingLandtypeSelectedOption, logScale, selectedKey]);

    return (
        <div className='compare'
            ref={CompareRef} >
            <h2>Compare Data</h2>
            <div className="compare__container_one">
                <div className='compare__container__content__item__content'>
                    {comparingLandtypeSelectedOption !== "none"
                        ? <p>Cumulative Data by {compareSelectedKey} for {selectedYearEditing.split('-')[0]} for {comparingLandtypeSelectedOption}</p>
                        : <p>Cumulative Data by {compareSelectedKey} for {selectedYearEditing.split('-')[0]}</p>
                    }
                    <canvas id="currentChart"></canvas>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={logScale}
                                onChange={() => setLogScale(!logScale)}
                                color="green"
                            />
                        }
                        style={{ paddingTop: '20px' }}
                    />
                    <p style={{ fontSize: '12px', display: 'inline-block', position: 'relative', top: '9px', left: '-10px' }}>Logarithmic Scale</p>
                </div>
            </div>
            <div className="compare__container_two">
                <div className='compare__container__content__item__content'>
                    {comparingLandtypeSelectedOption !== "none"
                        ? <p>Accumulative Data by {compareSelectedKey} across Timeseries for {comparingLandtypeSelectedOption}</p>
                        : <p>Accumulative Data by {compareSelectedKey} across Timeseries</p>
                    }
                    <canvas id="yearlyChart"></canvas>
                </div>
            </div>
        </div>
    );
};

export default ComparePanel;